import { memo, useCallback, useMemo } from 'react';
import { ErrorLoad, Table, TABLE_THEMES, TableBody } from '@wg/wows-react-uikit';

import { t } from '~/helpers/localization';

import StepCSSTransitionGroup from '~/UIKit/StepCSSTransitionGroup/StepCSSTransitionGroup';

import styles from './ClanBattlesTable.scss';
import ClanBattlesTableHead from './ClanBattlesTableHead';
import ClanBattlesTableItem from './ClanBattlesTableItem';
import BattleTeamsList from '../BattleTeamsList/BattleTeamsList';

import type { SEASON_TYPES } from '~/constants';
import type { IClanBattle } from '~/types/declaration';

interface ClanBattlesTableProps {
  battlesList: IClanBattle[];
  errorMessage: string;
  isError: boolean;
  isFetching: boolean;
  seasonType?: SEASON_TYPES;
  selectedBattle: number;
  stickyContainerId: string;
  onClickBattle: (id: number) => void;
}

const ClanBattlesTable = ({
  battlesList,
  errorMessage,
  isError,
  isFetching,
  seasonType,
  selectedBattle,
  stickyContainerId,
  onClickBattle,
}: ClanBattlesTableProps) => {
  const handleClickBattle = useCallback(
    (battleId: number, hasPlayers: boolean) => {
      if (hasPlayers) {
        onClickBattle(battleId);
      }
    },
    [onClickBattle],
  );

  const tableContent = useMemo(() => {
    if (isFetching) {
      return (
        <StepCSSTransitionGroup level={1}>
          <ErrorLoad isFlat message={t('Идёт загрузка истории боёв')} />
        </StepCSSTransitionGroup>
      );
    }

    if (isError) {
      return (
        <StepCSSTransitionGroup level={1}>
          <ErrorLoad isFlat message={errorMessage} />
        </StepCSSTransitionGroup>
      );
    }

    if (!battlesList.length) {
      return (
        <StepCSSTransitionGroup level={1}>
          <ErrorLoad isFlat message={t('В настоящее время твой клан не сыграл ни одного боя.')} />
        </StepCSSTransitionGroup>
      );
    }

    const rows = battlesList.map((battle, index) => {
      const [userTeam, enemyTeam] = battle.teams;

      const hasPlayers = Boolean(userTeam.players.length || enemyTeam.players.length);
      const isActive = battle.id === selectedBattle;

      return (
        <StepCSSTransitionGroup
          level={index}
          key={`battle_${battle.id}`}
          onClick={() => handleClickBattle(battle.id, hasPlayers)}
        >
          <ClanBattlesTableItem
            battleId={battle.id}
            date={battle.finished_at}
            ratingDelta={userTeam.rating_delta}
            result={userTeam.result}
            arrow={userTeam.stage ? userTeam.stage.type : ''}
            progress={userTeam.stage ? userTeam.stage.progress : []}
            map={battle.map ? battle.map.name : '-'}
            enemyRealm={enemyTeam.claninfo.realm}
            enemyName={enemyTeam.claninfo.name}
            enemyTag={enemyTeam.claninfo.tag}
            enemyTagColor={enemyTeam.claninfo.color}
            enemyTeamNumber={enemyTeam.team_number}
            isActive={isActive}
            target={userTeam.stage ? userTeam.stage.target : ''}
            seasonType={seasonType}
          />

          <BattleTeamsList userTeam={userTeam} enemyTeam={enemyTeam} isShow={isActive} />
        </StepCSSTransitionGroup>
      );
    });

    return (
      <StepCSSTransitionGroup level={1}>
        <TableBody key="table-body">{rows}</TableBody>
      </StepCSSTransitionGroup>
    );
  }, [battlesList, errorMessage, handleClickBattle, isError, isFetching, seasonType, selectedBattle]);

  return (
    <div className={styles.container}>
      <Table theme={TABLE_THEMES.BLUE} isFetching={isFetching} stickyContainerId={stickyContainerId}>
        <ClanBattlesTableHead stickyContainerId={stickyContainerId} />
        {tableContent}
      </Table>
    </div>
  );
};

export default memo(ClanBattlesTable);
